<template>
  <v-dialog v-bind="$attrs" :content-class="$style['help-overlay']">
    <v-sheet :class="$style.sheet" light>
      <Snackbar
        :status="status.code"
        :message="status.message"
        :timeout="timeout"
        :show-snackbar="showSnackbar"
      />
      <v-form ref="form" v-model="valid" @submit.prevent="request">
        <v-row>
          <v-col>
            <BaseHeading :class="$style.headline" :level="3">{{
              $t('help.form.headline')
            }}</BaseHeading>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <p>{{ $t('help.form.subline') }}</p>
          </v-col>
        </v-row>
        <v-container :class="$style['brand-container']">
          <v-row>
            <v-col :class="$style['brand-heading']"
              ><span :class="$style['required-indicator']">* </span> {{ $t('contact.field.brand') }}
            </v-col>
          </v-row>
          <v-row>
            <v-col :class="$style['brand-selection']">
              <v-radio-group v-model="brand" row :rules="brandRules" :hide-details="false" required>
                <v-radio
                  v-for="radio in radioItems"
                  :key="radio.value"
                  class="mb-4"
                  :value="radio.value"
                >
                  <template #label
                    ><BrandLogoRef
                      :brand="radio.brandPimId"
                      :class="$style.brand"
                      :title="radio.label"
                  /></template>
                </v-radio>
              </v-radio-group>
            </v-col>
          </v-row>
        </v-container>
        <v-row :class="$style['personal-details-contact']">
          <v-col>
            <BaseTextfield
              v-model="firstname"
              maxlength="30"
              :hide-details="false"
              :label="$t('help.form.field.firstname')"
              :rules="nameRules"
              required
            />
          </v-col>
          <v-col>
            <BaseTextfield
              v-model="surname"
              maxlength="30"
              :hide-details="false"
              :label="$t('help.form.field.surname')"
              :rules="nameRules"
              required
            />
          </v-col>
          <v-col>
            <BaseTextfield
              v-model="component"
              :hide-details="false"
              :label="$t('help.form.field.part')"
            />
          </v-col>
          <v-col>
            <BaseTextfield
              v-model="componentBrand"
              :hide-details="false"
              :label="$t('help.form.field.brand')"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-textarea
              v-model="msg"
              maxlength="255"
              counter
              :hide-details="false"
              :placeholder="$t('help.form.field.description.placeholder')"
              outlined
              :rules="msgRules"
              required
            >
              <template #label>
                <span :class="$style.label" class="required">{{
                  $t('help.form.field.description.label')
                }}</span>
              </template>
            </v-textarea>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <BaseHeading :level="3">{{ $t('help.form.contact') }}</BaseHeading>
          </v-col>
        </v-row>
        <v-row :class="$style['mode-of-contact']">
          <v-col>
            <BaseTextfield
              v-model="email"
              :hide-details="false"
              :label="$t('help.form.field.mail')"
              :rules="emailRules"
              required
            />
          </v-col>
          <v-col>
            <BaseTextfield
              v-model="tel"
              :hide-details="false"
              :label="$t('help.form.field.phone')"
              :rules="telRules"
              required
            />
          </v-col>
          <v-col />
          <v-col />
        </v-row>
        <v-row :class="$style['privacy-contact']">
          <v-col>
            <BaseCheckbox
              v-model="privacy"
              :class="[$style['privacy-hint'], $style.label]"
              :value="false"
              :rules="privacyRules"
              required
              >{{ $t('help.form.privacy', { link: privaryUrl }) }}</BaseCheckbox
            >
          </v-col>
        </v-row>
        <v-row :class="$style.controls">
          <v-btn
            color="primary"
            outlined
            type="button"
            rounded
            :elevation="$attrs.elevation || '0'"
            class="btn"
            :class="$style['cancel-contact']"
            @click.native="$emit('switchFormState', $event)"
            >{{ $t('help.form.cancel') }}</v-btn
          >
          <BaseButton :disabled="!valid" type="primary" :class="$style['submit-contact']">{{
            $t('help.form.submit')
          }}</BaseButton>
        </v-row>
      </v-form>
      <p :class="$style.required" class="required">{{ $t('help.form.required') }}</p>
    </v-sheet>
  </v-dialog>
</template>

<script>
import { get } from 'lodash-es'
import axios from '@/js/helper/axios-helper.js'
import { mapGetters } from 'vuex'

import { types as STOREMODULES } from '@/js/plugins/store/store-modules.js'

import { getStatus } from '@/js/helper/request-validator-helper.js'

import APP_GETTER from '@/js/plugins/store/modules/app/getter-types.js'

import helpErrors from '@/js/components/Help/mixins/help-error-mixin.js'
import Snackbar from '@/js/components/Shared/Snackbar.vue'
// import { camelCase } from 'change-case'
import contactErrors from '@/js/components/Contact/mixins/contact-error-mixin.js'
import BrandLogoRef from '@/js/components/Shared/BrandLogoRef.vue'
import { BRAND_TO_BRAND_LONG } from '@/js/helper/dp-usermanagement-constants'

export default {
  name: 'HelpForm',
  components: {
    Snackbar,
    BrandLogoRef,
  },
  mixins: [helpErrors, contactErrors],
  data: () => ({
    brand: undefined,
    value: false,
    valid: false,
    firstname: '',
    surname: '',
    email: '',
    tel: '',
    msg: '',
    component: '',
    componentBrand: '',
    privacy: false,
    status: {},
    defaultBrands: Object.entries(BRAND_TO_BRAND_LONG)
      .map(([brand, long]) => {
        brand, long
      })
      .filter(({ long }) => [BRAND_TO_BRAND_LONG.KH, BRAND_TO_BRAND_LONG.FO].includes(long)),
    timeout: 5000,
    showSnackbar: false,
  }),
  computed: {
    ...mapGetters(STOREMODULES.APP, {
      getLinkByType: APP_GETTER.APP_CONFIG_LINK_BY_TYPE,
      brands: APP_GETTER.USER_BRANDS,
      twoLetterLocale: APP_GETTER.LOCALE,
    }),
    privaryUrl() {
      return this.getLinkByType('privacy')
    },
    radioItems() {
      return (this.brands.length ? this.brands : this.defaultBrands).map(({ brand, long }) => {
        return {
          value: long,
          label: this.$t(`filter.items.ItemBrand.title.${brand}`),
          brandPimId: this.$t(`settings.brand.nameToPimId.${long}`),
          // settings.brand.nameToPimId.univega
          // settings.brand.nameToPimId.santacruz
          // settings.brand.nameToPimId.rixe
          // settings.brand.nameToPimId.raleigh
          // settings.brand.nameToPimId.kalkhoff
          // settings.brand.nameToPimId.juliana
          // settings.brand.nameToPimId.focus
          // settings.brand.nameToPimId.cervelo
          // settings.brand.nameToPimId.urbanarrow
        }
      })
    },
  },
  methods: {
    requestParams() {
      return {
        name: this.firstname,
        surname: this.surname,
        language: this.twoLetterLocale,
        brand: this.brand,
        email: this.email,
        phone: this.tel,
        message: this.msg,
        privacy: this.privacy,
        component: this.component,
        componentBrand: this.componentBrand,
      }
    },
    request() {
      if (!this.valid) {
        return
      }

      const url = '/api/buhelpformapi?code=create-help'
      const data = this.requestParams()

      axios
        .post(url, data)
        .then(response => {
          this.requestData = response.data
          this.status = getStatus(this.requestData)

          this.setStatusMessage()

          if (this.status.code === 200) {
            this.reset()
          }
        })
        .catch(error => {
          // Todo: Handle error better?
          const message = get(error, ['response', 'data', 'error', 'message'])
          if (message) {
            this.status.message = message
            this.showSnackbar = true
          }
          console.log(error)
        })
    },
    setStatusMessage() {
      this.status.message = this.$t('form.message.success')
    },
    reset() {
      this.$refs.form.reset()
      this.$emit('submitForm', {
        status: this.status,
        showSnackbar: true,
      })
    },
  },
}
</script>

<style lang="scss" module>
.help-overlay {
  @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
    margin: 24px;
  }
  top: 0 !important;
  z-index: 5;

  margin: 10px;
}

.alert {
  justify-content: left;
}

.sheet {
  @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
    padding: 1.75rem;
  }
  min-width: 60vw;
  margin: auto;
  padding: 0.7rem;
}

.headline {
  margin-bottom: 0;
}

.label {
  color: var(--black);

  background: var(--white);

  *:not(a) {
    color: var(--black);
  }
}

.required {
  @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
    margin-top: 0;
  }
  margin-top: 16px;

  color: var(--red);
  font-size: var(--font-sm);
}

:global(.v-input--is-focused) .label,
:global(.v-input--is-focused) .label *:not(a) {
  color: currentColor;
}

.controls {
  @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
    display: grid;
    grid-template-rows: repeat(4, auto);
    grid-template-columns: 1fr repeat(2, minmax(12rem, 16rem)) 1fr;
    margin: -12px;
  }

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin: auto;

  & > * {
    @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
      margin: 0 1rem;
    }
    margin: 0.8rem 0 0 0;
  }

  button:first-child {
    grid-column: 2;
  }

  button:last-child {
    grid-column: 3;
  }
}

.privacy-hint {
  align-items: flex-start;
  margin-top: 0.2rem;
}

.brand {
  order: 1;
  width: 45%;
}

.brand-container {
  max-width: 100%;
  margin-bottom: 24px;
  padding-top: 12px !important;
  padding-bottom: 0 !important;

  border: 1px solid #819ea8;
}

.brand-heading {
  padding-bottom: 0;
}

.brand-selection {
  padding-top: 0;

  :global(.v-input__slot) {
    margin-bottom: 0;
  }
}

.required-indicator {
  color: #e03537;
}

.personal-details-contact,
.mode-of-contact {
  @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
    flex-direction: row;
  }
  flex-direction: column;

  :global(.col) {
    @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
      padding: 12px;
    }
    padding: 3px 12px;

    &:first-child {
      padding-top: 12px;
    }
  }
}

.privacy-contact {
  :global(.v-input__slot) {
    @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
      align-items: center;
    }
    align-items: flex-start;
  }
}

.cancel-contact,
.submit-contact {
  align-self: stretch;
}
</style>
